import { useRef } from 'react'
import type React from 'react'
import type { WireColor } from '@/api/types/color'
import { cn } from '@/utils'
import colorNoneSvg from '@/assets/color-none.svg'
import { tintedPaletteHex } from '@/lib/colors/resolve-palette-color'
import { isNoneColor } from '@/lib/colors/fill-paint'
import { useColorSwatchDrag } from '../hooks/use-color-swatch-drag'
import { ColorSwatch } from './color-swatch'

interface ColorPopoverColorListProps {
  colors: WireColor[]
  colorLayout: 'grid' | 'list'
  internalColorId: number | null
  styleColorIds: Set<number>
  listRef: React.RefObject<HTMLDivElement | null>
  listHeight: number
  onColorSelect: (color: WireColor) => void
  /** A drag-to-fill started from one of the listed colors. */
  onColorDragStart?: (color: WireColor) => void
}

/** One row in the list-view color list — uses the drag hook so it also supports drag-to-fill. */
function ColorListItem({
  color,
  isSelected,
  isStyleColor,
  onColorSelect,
  onColorDragStart,
}: {
  color: WireColor
  isSelected: boolean
  isStyleColor: boolean
  onColorSelect: (color: WireColor) => void
  onColorDragStart?: (color: WireColor) => void
}) {
  const chipRef = useRef<HTMLDivElement>(null)
  const { onPointerDown, onPointerMove, onPointerUp, onPointerCancel } = useColorSwatchDrag(
    color,
    100,
    chipRef,
    () => onColorDragStart?.(color)
  )

  return (
    <button
      key={color.colorId}
      type="button"
      aria-label={color.colorName}
      aria-pressed={isSelected}
      onClick={() => onColorSelect(color)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      className={cn(
        'flex items-center gap-2 h-8 w-full pl-1.5 pr-2.5 rounded-sm transition-colors',
        isSelected ? 'bg-secondary' : 'hover:bg-muted'
      )}
    >
      <div
        ref={chipRef}
        className="flex items-center justify-center size-8 p-1 rounded-md ring-2 ring-inset ring-muted bg-popover shrink-0 overflow-hidden"
      >
        {isNoneColor(color) ? (
          <img src={colorNoneSvg} alt="None" className="size-6 block" draggable={false} />
        ) : (
          <span
            className="block size-6 rounded-[2px] border border-foreground shrink-0"
            style={{ backgroundColor: tintedPaletteHex(color, 100) ?? color.hexValue }}
          />
        )}
      </div>
      <span className="text-sm font-normal text-foreground truncate">{color.colorName}</span>
      {isStyleColor && (
        <span className="flex items-center justify-center h-5 px-2 rounded-md border border-border bg-background shrink-0">
          <span className="text-xs font-semibold text-foreground">Style</span>
        </span>
      )}
    </button>
  )
}

export function ColorPopoverColorList({
  colors,
  colorLayout,
  internalColorId,
  styleColorIds,
  listRef,
  listHeight,
  onColorSelect,
  onColorDragStart,
}: ColorPopoverColorListProps) {
  if (colorLayout === 'list') {
    return (
      <div
        ref={listRef}
        style={{ maxHeight: listHeight }}
        className="flex flex-col px-1 overflow-y-auto"
      >
        {colors.map((color) => (
          <ColorListItem
            key={color.colorId}
            color={color}
            isSelected={color.colorId === internalColorId}
            isStyleColor={styleColorIds.has(color.colorId)}
            onColorSelect={onColorSelect}
            onColorDragStart={onColorDragStart}
          />
        ))}
      </div>
    )
  }

  return (
    <div
      ref={listRef}
      style={{ maxHeight: listHeight }}
      className="flex flex-wrap px-1 overflow-y-auto"
    >
      {colors.map((color) => (
        <ColorSwatch
          key={color.colorId}
          color={color}
          isSelected={color.colorId === internalColorId}
          isStyleColor={styleColorIds.has(color.colorId)}
          onClick={onColorSelect}
          onDragStart={onColorDragStart}
        />
      ))}
    </div>
  )
}
